import { useState, useCallback } from 'react';
import { useSelectedStore } from '@/contexts/StoreContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAppointmentsPaginated, useStores, useUpdateAppointment, usePagination } from '@/hooks';
import type { AppointmentStatus } from '@geely/shared';
import { toast } from 'sonner';
import { appointmentsApi } from '@/lib/api/appointments.api';
import { exportCSV, exportExcel, exportPDF } from '@/lib/export';

export function useAppointmentsData() {
  const { selectedStoreId: userStoreId, userStoreIds, isMultiStore } = useSelectedStore();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { page, limit, setPage, setLimit, resetPage } = usePagination();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const showStoreFilter = isAdmin || isMultiStore;

  const { data: allStores } = useStores();

  const availableStores = isAdmin
    ? (allStores ?? [])
    : (allStores ?? []).filter((s) => userStoreIds.includes(s.id));

  const { data: paginatedResult, isLoading } = useAppointmentsPaginated({
    storeId: !showStoreFilter ? (userStoreId ?? undefined) : undefined,
    storeIds: storeFilter.length > 0 ? storeFilter : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: search || undefined,
    sortBy,
    sortOrder: sortBy ? sortOrder : undefined,
    page,
    limit,
  });

  const appointments = paginatedResult?.data ?? [];
  const paginationMeta = paginatedResult?.meta;

  const updateAppointment = useUpdateAppointment();

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    resetPage();
  };

  const handleDateFromChange = (date: string) => {
    setDateFrom(date);
    resetPage();
  };

  const handleDateToChange = (date: string) => {
    setDateTo(date);
    resetPage();
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    resetPage();
  };

  const handleStoreFilterChange = (ids: string[]) => {
    setStoreFilter(ids);
    resetPage();
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    resetPage();
  };

  const handleStatusChange = async (appointmentId: string, newStatus: AppointmentStatus) => {
    try {
      await updateAppointment.mutateAsync({ id: appointmentId, data: { status: newStatus } });
      toast.success('Status atualizado com sucesso!');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao atualizar status');
    }
  };

  const handleExport = useCallback(async (format: 'csv' | 'excel' | 'pdf') => {
    try {
      const res = await appointmentsApi.list({
        storeId: !showStoreFilter ? (userStoreId ?? undefined) : undefined,
        storeIds: storeFilter.length > 0 ? storeFilter : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        sortBy,
        sortOrder: sortBy ? sortOrder : undefined,
      });
      const data = res.data;
      if (format === 'csv') exportCSV(data);
      else if (format === 'excel') exportExcel(data);
      else exportPDF(data, { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined });
    } catch {
      toast.error('Erro ao exportar agendamentos');
    }
  }, [showStoreFilter, userStoreId, storeFilter, dateFrom, dateTo, statusFilter, search, sortBy, sortOrder]);

  const statuses: { value: AppointmentStatus; label: string }[] = [
    { value: 'pending', label: 'Pendente' },
    { value: 'in_progress', label: 'Em andamento' },
    { value: 'completed', label: 'Concluído' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'customer_absent', label: 'Cliente ausente' },
  ];

  return {
    isLoading,
    appointments,
    paginationMeta,
    setPage,
    limit,
    setLimit,
    statuses,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    dateFrom,
    dateTo,
    setDateFrom: handleDateFromChange,
    setDateTo: handleDateToChange,
    search,
    setSearch: handleSearchChange,
    showStoreFilter,
    availableStores,
    storeFilter,
    setStoreFilter: handleStoreFilterChange,
    sortBy,
    sortOrder,
    handleSortChange,
    handleStatusChange,
    handleExport,
  };
}
